/**
 * Navigation loading indicator (ASwap semi-SPA).
 *
 * ASwap turns internal navigation into a semi-SPA: clicking a link fires an XHR
 * and swaps the page content in place — no full reload. On a fast connection
 * that happens in a blink, so any spinner would only flash distractingly. But on
 * a slow / flaky mobile connection the request can hang for seconds with zero
 * feedback and the page looks frozen.
 *
 * This bridges that gap. We arm a timer when the request actually goes out
 * (`onRequestLoad`) and only reveal the indicator if the request is STILL in
 * flight after DELAY_MS — at which point `<main>` washes lighter and a small
 * spinner eases in from behind the bottom-right corner. The moment the response
 * arrives (`onRequestLoaded`) we either cancel the pending reveal (fast case —
 * nothing ever shows) or ease the indicator back out (slow case).
 *
 * Cached navigations never call `onRequestLoad` (ASwap serves them synchronously
 * from its result cache), so the indicator never even arms for them.
 *
 * The visuals are pure CSS, keyed off `html.is-nav-loading`; this module only
 * toggles that class on a delay. See the `nav-loading` section in
 * interactions.css and the persistent markup in `_boxes/nav_loading.php`.
 *
 * Wired from the ASwap event handlers in `aswap.ts`.
 */

// Grace period before the indicator appears. A navigation that resolves within
// this window is treated as "instant" and stays silent — only genuinely slow
// requests reveal the spinner, so normal fast loads never flash it. Tweak this
// single value to make the indicator more / less eager.
const DELAY_MS = 450;

const LOADING_CLASS = 'is-nav-loading';

let timer: number | undefined;

/** Request sent — arm the delayed reveal. */
export const navLoadingStart = (): void => {
  clearTimeout(timer);
  timer = window.setTimeout(() => {
    document.documentElement.classList.add(LOADING_CLASS);
  }, DELAY_MS);
};

/** Response received — cancel a pending reveal (fast) or hide the indicator (slow). */
export const navLoadingEnd = (): void => {
  clearTimeout(timer);
  timer = undefined;
  document.documentElement.classList.remove(LOADING_CLASS);
};
